import type { ApiClient } from '@repo/api-client';
import {
  type CostOfGoodsResource,
  type OutletTradingSummaryResource,
  ProfitAndLossController_park_v1,
  ProfitAndLossController_parkTradingSummary_v1,
  type ProfitAndLossReportResource,
  type TradingSummaryReportResource,
  type TradingSummaryResource,
} from '@repo/api-client';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { type ReactNode, useEffect, useId, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { isForbidden } from '@/api/failure';
import { useSession } from '@/auth/session';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableSkeleton,
} from './back-office/Screen';
import { costLines } from './reporting/profit-and-loss-cost';
import { currentBusinessDate } from './reporting/reporting-period';
import { formatBusinessDate } from './tickets/business-date';

/**
 * The signed-in landing screen: what the park has taken today, outlet by outlet.
 *
 * Every role lands here — `route-table.tsx` declares it `requireSignedIn()` —
 * and until now it was the first week's scaffold: a heading, a hardcoded
 * `₹1,250.00` and a **Take payment** button that did nothing. The figures it
 * should have been carrying already ship.
 *
 * ## Today, and why the period is not a choice here
 *
 * `from` and `to` are required on both routes and there is no "since forever",
 * so the screen has to pick one. It picks **the park's current trading day,
 * from and to** — `currentBusinessDate` in Asia/Kolkata, per R-006, off the
 * injected clock and never the device's local getters.
 *
 * That is the question a landing screen answers. Somebody opening the back
 * office mid-shift wants to know how the day is going, and the API serves
 * exactly that: **today's still-open trading is included rather than appearing
 * only at close**, so the figures move through the afternoon instead of being
 * yesterday's until somebody closes the day. A month of history is a different
 * question and it already has two screens — Profit and loss for an owner,
 * Trading summary for a manager — each with its own pickers and its own export.
 * Putting a third set of pickers on the landing screen would make every arrival
 * a decision, and the screen would stop being an answer.
 *
 * The day is read once per render rather than held in state, so a back office
 * left open across midnight moves to the new trading day rather than reporting
 * the one it opened on.
 *
 * ## Two branches, and they are not one screen with a role flag
 *
 * **An owner reads the park in one request.**
 * `GET /v1/reports/profit-and-loss` declares `@RequireRole('OWNER')` and answers
 * with `park` **and** `outlets[]` together, so the total and the breakdown cost
 * one round trip between them. That is this screen's reason to exist.
 *
 * **Everybody else reads the outlets they reach, also in one request.**
 * `GET /v1/reports/trading-summary` is `@RequireOutletScope()` and margin-free,
 * and answers with `park` and `outlets[]` together the same way — so the two
 * branches draw the same plate and the same rows, and the only difference is
 * the cost band the owner's resource carries and this one does not.
 *
 * **No total is computed here.** `park` on that route is summed by the server
 * over exactly the outlets the caller reaches (`blossom-rest-api#478`), so for a
 * manager confined to three outlets it is the total over those three and not
 * the park's — which is why the band is named for what it covers rather than
 * called the park. Until that route existed this branch read every outlet one
 * request at a time and showed no total at all, because summing in the browser
 * would have been a second source of truth; `docs/ui-conventions.md` says the
 * client does not compute totals, and that stays true.
 *
 * ## Every branch handles `AUTH_FORBIDDEN` arriving anyway
 *
 * The branch is chosen on `session.user.role`, which is restored from
 * `sessionStorage`, validated for shape and not for truth, and editable by
 * anybody with the developer tools open. So it decides what is *drawn* and
 * never what is *permitted*: either read can still be refused. Both land in `failed` and are worded as a refusal
 * rather than as a fault.
 *
 * **A refused owner read deliberately does not fall back** to the margin-free
 * read. It could — the server would permit whatever it permits — but the
 * remedy for a role this browser is holding out of date is to sign in again,
 * and a screen that silently rendered the lesser report instead would hide the
 * fact that the session is stale rather than say it.
 *
 * The route's own `access` stays `requireSignedIn()`. The landing screen has to
 * stay reachable by anybody signed in; what varies is what it *shows*.
 *
 * ## Nothing here is computed
 *
 * `netPaise` and `grossMarginPaise` are derived by the server on every read and
 * stored nowhere (R-701). The figures render as sent. If the terms ever fail to
 * add up to the net beside them, the honest thing is that the discrepancy is
 * visible — a screen that quietly agreed with its own arithmetic would be the
 * one place a contract defect could never be found. `ProfitAndLoss.tsx` makes
 * the same argument at length and this screen follows it rather than restating
 * it.
 */

/**
 * The four figures both report resources publish for one outlet.
 *
 * Derived with `Pick` from the generated `OutletTradingSummaryResource` rather
 * than written out, so this is a *view* of an API shape and not a second
 * statement of one — a renamed field fails to compile here.
 * `OutletProfitAndLossResource` is assignable to it, which is what lets the
 * owner's rows and everybody else's go through one table: the two resources
 * carry the same nine fields and the owner's adds `costOfGoods`.
 *
 * Cost and margin are **not** in the table, for either role. The split between
 * the two routes is exactly cost and margin, and keeping the breakdown to the
 * four figures both of them publish means the table is the same table whoever
 * is reading it — the difference between the roles is the cost band above it,
 * which is precisely the difference the API makes.
 */
type OutletLine = Pick<
  OutletTradingSummaryResource,
  | 'outletId'
  | 'outletName'
  | 'goodsRevenuePaise'
  | 'admissionRevenuePaise'
  | 'expensesPaise'
  | 'netPaise'
>;

/** Every state the screen can be in. One value, so two cannot coexist. */
type OverviewState =
  | { readonly kind: 'loading' }
  /** The owner's read: the park's own figures and every outlet under them. */
  | { readonly kind: 'park'; readonly report: ProfitAndLossReportResource }
  /** Everybody else's: the outlets in reach and the server's total over them. */
  | { readonly kind: 'reach'; readonly report: TradingSummaryReportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** This screen's own abort, from a filter change or an unmount. Not a failure. */
const isAbort = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

/** The owner's read. One request, the park and its outlets together. */
async function readTheWholePark(
  client: ApiClient,
  day: string,
  signal: AbortSignal,
): Promise<OverviewState> {
  const report = await client.request(ProfitAndLossController_park_v1, {
    query: { from: day, to: day },
    signal,
  });

  return { kind: 'park', report };
}

/** Everybody else's read. One request, the outlets in reach and their total. */
async function readTheOutletsInReach(
  client: ApiClient,
  day: string,
  signal: AbortSignal,
): Promise<OverviewState> {
  const report = await client.request(ProfitAndLossController_parkTradingSummary_v1, {
    query: { from: day, to: day },
    signal,
  });

  return { kind: 'reach', report };
}

export function Root() {
  const client = useApiClient();
  const { session } = useSession();

  // Cosmetic, and only ever cosmetic: it picks which request to make, and the
  // server decides what comes back. See the note at the head of this file.
  const readsTheParkTotal = session?.user.role === 'OWNER';

  // The park's own day, in Asia/Kolkata — not the device's. Read once per
  // render rather than held in state, so a screen left open across midnight
  // moves to the new trading day.
  const today = currentBusinessDate(systemClock);

  const [state, setState] = useState<OverviewState>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    const read = readsTheParkTotal
      ? readTheWholePark(client, today, controller.signal)
      : readTheOutletsInReach(client, today, controller.signal);

    read
      .then((next) => {
        if (live) setState(next);
      })
      .catch((error: unknown) => {
        if (live && !isAbort(error)) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, readsTheParkTotal, today, reloads]);

  return (
    <Screen
      title="Overview"
      lede="The park today, outlet by outlet"
      description={
        readsTheParkTotal
          ? "What the park has taken today so far and what each outlet contributed, with trading still open counted as it stands rather than waiting for the day to close. The park's own total is the server's answer, taken as sent. A longer period, the refunds handed back and the full statement are on Profit and loss."
          : "What the outlets this account reaches have taken today so far and what each contributed, with trading still open counted as it stands. The total is the server's answer over those outlets, taken as sent. A longer period, one outlet at a time, is the Trading summary."
      }
      aside={
        <>
          <ScreenFact label="Trading day">
            {formatBusinessDate(today)}
            <br />
            <span className="text-muted-foreground">
              Today as it stands. Trading still open is counted.
            </span>
          </ScreenFact>
        </>
      }
    >
      {state.kind === 'loading' && <OverviewSkeleton withCost={readsTheParkTotal} />}

      {state.kind === 'failed' && (
        <Notice>
          {isForbidden(state.error) ? (
            readsTheParkTotal ? (
              <EmptyState
                title="This account cannot read the park's figures"
                description="What the goods cost and the margin on them are the owner's alone, and the server refuses everybody else before the report is built. If you are the owner, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : (
              <EmptyState
                title="This account cannot read today's trading"
                description="Reading an outlet's trading is the owner's and the manager's. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            )
          ) : (
            <LoadFailed
              error={state.error}
              onRetry={() => {
                setReloads((count) => count + 1);
              }}
              title="Today's figures could not be read"
            />
          )}
        </Notice>
      )}

      {state.kind === 'park' && (
        <>
          <ParkTotal
            name="The whole park"
            figures={state.report.park}
            costOfGoods={state.report.park.costOfGoods}
          />
          <OutletBreakdown lines={state.report.outlets} />
        </>
      )}

      {state.kind === 'reach' &&
        (state.report.outlets.length === 0 ? (
          <Notice>
            <EmptyState
              title="No outlet is in reach from this account"
              description="Today's takings are reported outlet by outlet, and this account reaches none. Somebody who staffs the park sets the outlets up and posts people to them."
            />
          </Notice>
        ) : (
          <>
            <ParkTotal name="Every outlet in reach" figures={state.report.park} />
            <OutletBreakdown lines={state.report.outlets} />
          </>
        ))}
    </Screen>
  );
}

/**
 * The total for the day — the park's for an owner, the outlets in reach for
 * everybody else — and the reason each branch is one request rather than eleven.
 *
 * ## Why these read across and not down
 *
 * `ProfitAndLoss` sets its statement as a column of terms running to a net,
 * which is right there: that screen is a statement, read down, and the outlets
 * under it are a comparison somebody scrolls to on purpose.
 *
 * This screen is the landing screen and its whole claim is **the total and the
 * breakdown together**. Rendered as a column at 1280x800 — the desk browser the
 * back office is measured at — the park's figures took the entire records band
 * and pushed all eleven outlets below the fold, which is the one thing this
 * screen cannot afford to do. Laid across, the figures cost one band and the
 * outlets start under them.
 *
 * The tiles are the plate device — a signage label over the value it names —
 * which `ScreenFact`, `ScreenNote` and `AuthFrame.Fact` are each already an
 * instance of. `docs/design-language.md` says in as many words that the two
 * surfaces must not invent it twice, so this is not a third. It stays a `<dl>`
 * underneath, because these are labelled values and `<dt>`/`<dd>` is what pairs
 * a label with its value for somebody reading with a screen reader.
 *
 * ## What is in the net, and what is deliberately not
 *
 * The four terms beside the net are the terms *in* the net: goods, admission
 * and hand-entered admission less expenses. Refunds are not among them —
 * `goodsRevenuePaise` arrives already net of the refunds counted in the same
 * period — so a tile for them here would invite subtracting them twice. They
 * are a disclosure with a paragraph of their own on Profit and loss (or Trading
 * summary, for a manager), which is where this screen sends somebody who wants
 * them.
 *
 * The cost band below the rule — the owner's alone, drawn only when
 * `costOfGoods` is passed — is separate because gross margin is a different
 * question from the net rather than a continuation of it: it is goods revenue
 * less what those goods cost, with no admission in it at all, and setting the
 * two in one run of figures would invite reading them as one sum.
 */
function ParkTotal({
  name,
  figures,
  costOfGoods,
}: {
  name: string;
  figures: TradingSummaryResource;
  costOfGoods?: CostOfGoodsResource;
}) {
  const cost = costOfGoods && costLines(costOfGoods);

  return (
    <Panel name={name}>
      {/* Five across on a desk, folding to three and then to two. The net is
          last because it is what the four before it come to, and it is the one
          the eye should end on. */}
      <dl className="grid grid-cols-2 gap-x-8 gap-y-5 md:grid-cols-3 xl:grid-cols-5">
        <Figure term="Goods sold">{figures.goodsRevenuePaise}</Figure>
        <Figure term="Admission">{figures.admissionRevenuePaise}</Figure>
        <Figure term="Entered by hand">{figures.assertedAdmissionRevenuePaise}</Figure>
        <Figure term="Expenses">{figures.expensesPaise}</Figure>
        <Figure term="Net so far today" strong>
          {figures.netPaise}
        </Figure>
      </dl>

      <div className="mt-5 border-t border-border pt-4">
        {cost?.kind === 'figures' && (
          <dl className="grid grid-cols-2 gap-x-8 gap-y-5 md:grid-cols-3 xl:grid-cols-5">
            <Figure term="Cost of goods sold">{cost.costOfGoodsSoldPaise}</Figure>
            <Figure term="Gross margin" strong>
              {cost.grossMarginPaise}
            </Figure>
          </dl>
        )}

        {cost?.kind === 'no-goods' && (
          <p className="text-sm text-muted-foreground">
            No goods have been sold today, so there is no cost of goods and no margin. That is not a
            cost of zero: zero would say the goods sold cost the park nothing.
          </p>
        )}

        {cost?.kind === 'inconsistent' && (
          <p role="status" className="text-sm text-destructive">
            The cost of goods on this report contradicts what it says about itself, so it is not
            shown rather than being guessed at. Report it — nothing else above is affected.
          </p>
        )}

        <p className={`${cost ? 'mt-4' : ''} min-h-10 text-sm text-muted-foreground`}>
          Every figure is the server&rsquo;s, taken as sent.{' '}
          {cost?.kind === 'figures' &&
            'The margin has no admission in it and is never added to the net. '}
          {figures.assertedAdmissionCount > 0 &&
            `Entered by hand is ${String(figures.assertedAdmissionCount)} ${figures.assertedAdmissionCount === 1 ? 'admission' : 'admissions'} a person put into the books after a till could not send them — outside the admission beside it, inside the net. `}
          Refunds are already out of goods sold, and are set out on{' '}
          {cost ? 'Profit and loss' : 'Trading summary'}.
        </p>
      </div>
    </Panel>
  );
}

/**
 * Every outlet's day, in the four figures both report routes publish.
 *
 * **Keyed on `outletId`.** `outletName` is not unique and never has been — two
 * live outlets may share a name, and retiring an outlet frees its code for
 * reuse — so a row keyed on the name would drop one of two shops and leave
 * the total above disagreeing with the outlets under
 * it by whatever that shop took.
 *
 * An outlet that has not traded yet today is present with zeroes rather than
 * absent. At nine in the morning that is most of them, and it is the honest
 * answer: the day has not happened yet.
 *
 * The last column is an amount, so the table takes `measured` — the default —
 * and the figures right-align against the field's own edge, which is what
 * `docs/ui-conventions.md` asks of a column of money.
 */
function OutletBreakdown({ lines }: { lines: readonly OutletLine[] }) {
  return (
    <>
      <ScreenTable label="Outlets today">
        <TableHeader>
          <TableRow>
            <TableHead>Outlet</TableHead>
            <TableHead className="text-right">Goods sold</TableHead>
            <TableHead className="text-right">Admission</TableHead>
            <TableHead className="text-right">Expenses</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.outletId}>
              <TableCell>{line.outletName}</TableCell>
              <Amount>{line.goodsRevenuePaise}</Amount>
              <Amount>{line.admissionRevenuePaise}</Amount>
              <Amount>{line.expensesPaise}</Amount>
              <Amount>{line.netPaise}</Amount>
            </TableRow>
          ))}
        </TableBody>
      </ScreenTable>

      <p className="text-sm text-muted-foreground">
        The figures above are these outlets summed, not a separate question. Two rows may carry the
        same name: retiring an outlet frees its code for reuse, and both keep reporting what they
        took.
      </p>
    </>
  );
}

/** A right-aligned amount cell, in the column treatment money gets everywhere. */
function Amount({ children }: { children: number }) {
  return <TableCell className="text-right font-mono numeric">{formatPaise(children)}</TableCell>;
}

/**
 * One of the park's figures: a signage label over the amount it names.
 *
 * The plate device the whole system already uses — `ScreenFact` in the toolbar,
 * `ScreenNote` in a form's margin, `AuthFrame.Fact` in the signed-out rail —
 * carrying an amount instead of a sentence. Amounts take `font-mono` and the
 * `numeric` utility per `docs/ui-conventions.md`: Geist's default figures are
 * proportional, and these sit in a grid where the columns must line up.
 *
 * `strong` steps one weight and changes the label's colour, which is the whole
 * of the emphasis this system has. Nothing here grows — the page has one size
 * increase and it is spent on the title.
 *
 * **This is the fourth screen to want `Panel`.** `ProfitAndLoss.tsx` said it was
 * the third and that promoting it to `back-office/Screen.tsx` beside
 * `ScreenFact` was overdue but would touch two other epics' screens mid-flight.
 * The same is true here and this ticket is the Overview, so the move is named in
 * the PR body rather than taken.
 */
function Figure({
  term,
  children,
  strong = false,
}: {
  term: string;
  children: number;
  strong?: boolean;
}) {
  return (
    <div>
      <dt
        className={`font-display text-xs font-semibold tracking-[0.16em] uppercase ${strong ? 'text-foreground' : 'text-muted-foreground'}`}
      >
        {term}
      </dt>
      <dd className={`mt-1.5 font-mono numeric ${strong ? 'font-medium' : ''} text-foreground`}>
        {formatPaise(children)}
      </dd>
    </div>
  );
}

/**
 * A band of the page, named so the park's figures are a landmark a reader can
 * jump to.
 */
function Panel({ name, children }: { name: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId} className="pt-2">
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {name}
        </h2>
      </header>
      <div className="pt-5">{children}</div>
    </section>
  );
}

/**
 * A card carrying one of the states where there are no figures to show.
 *
 * No heading — `EmptyState` and `LoadFailed` each supply their own. The centring
 * and the measure are `TableNotice`'s, which is where every list screen in the
 * app puts these two, so a screen with nothing on it reads the way a list with
 * no rows does rather than stranding one sentence against the toolbar.
 */
function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center py-14">
      <div className="mx-auto max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

/**
 * The screen's shape while it is in flight.
 *
 * A skeleton and not a spinner, matching the content it will be replaced by, so
 * the page does not jump when the figures land. The cost band is drawn only for
 * the branch that will have one — a manager whose screen briefly showed a
 * margin's outline would be told to expect something the report does not carry.
 *
 * `TableSkeleton` inside the real table, so the column headers stay put and the
 * table keeps its accessible name throughout.
 */
function OverviewSkeleton({ withCost }: { withCost: boolean }) {
  return (
    <>
      {/* The band's own shape — a heading, five tiles, the rule, two more for
          the owner — rather than a slab of grey the height of a guess. A
          skeleton that is not the shape of what replaces it is a layout shift
          with extra steps. */}
      <div aria-hidden className="pt-2">
        {/* Every run is set in a box the height of the line box it stands in
              — 24px for the heading, 16 and 24 for a tile's label and value, 20
              for a line of the note — so the band is the height it will be and
              the table below does not move when the figures land. Slim bars in
              tall boxes, rather than tall bars, because a 24px slab reads as a
              chart rather than as text. */}
        <div className="border-b border-border pb-3">
          <Line height="h-6">
            <Bar className="h-4 w-40" />
          </Line>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-5 pt-5 md:grid-cols-3 xl:grid-cols-5">
          {Array.from({ length: 5 }, (_, tile) => (
            <TileSkeleton key={tile} />
          ))}
        </div>

        <div className="mt-5 border-t border-border pt-4">
          {withCost && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-5 md:grid-cols-3 xl:grid-cols-5">
              <TileSkeleton />
              <TileSkeleton />
            </div>
          )}

          {/* Two runs, because the note below the figures is two lines in the
                case this screen is usually in: nothing entered by hand, which is
                a rare and high-trust event. A third line lands when there was
                one, and the six pixels that costs are not worth a skeleton that
                lies about the common case. */}
          <div className={withCost ? 'mt-4' : ''}>
            <Line height="h-5">
              <Bar className="h-3.5 w-full" />
            </Line>
            <Line height="h-5">
              <Bar className="h-3.5 w-2/5" />
            </Line>
          </div>
        </div>
      </div>

      <ScreenTable label="Outlets today">
        <TableHeader>
          <TableRow>
            <TableHead>Outlet</TableHead>
            <TableHead className="text-right">Goods sold</TableHead>
            <TableHead className="text-right">Admission</TableHead>
            <TableHead className="text-right">Expenses</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableSkeleton columns={5} rows={5} />
      </ScreenTable>
    </>
  );
}

/** One placeholder run. Rounded like everything else; nothing here is square. */
function Bar({ className }: { className: string }) {
  return <div className={`animate-pulse rounded-3xl bg-muted ${className}`} />;
}

/** A figure's shape while it is in flight: the label's run over the value's. */
function TileSkeleton() {
  return (
    <div>
      <Line height="h-4">
        <Bar className="h-3 w-24" />
      </Line>
      <Line height="h-6" className="mt-1.5">
        <Bar className="h-4 w-28" />
      </Line>
    </div>
  );
}

/** A run set in the line box it stands for, so the skeleton is the right height. */
function Line({
  height,
  className = '',
  children,
}: {
  height: string;
  className?: string;
  children: ReactNode;
}) {
  return <div className={`flex items-center ${height} ${className}`}>{children}</div>;
}
